import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FaSearch,
  FaCalendar,
  FaClock,
  FaEye,
  FaUser,
  FaMoneyBillWave,
  FaMapMarkerAlt,
  FaDownload,
  FaFilter,
  FaImage,
  FaVideo,
  FaTag,
  FaGlobe,
  FaBuilding,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaArrowLeft,
  FaArrowRight,
  FaPlay,
  FaPause,
  FaUsers,
  FaChartBar,
  FaTrophy,
  FaStar,
  FaTimes,
  FaInfoCircle,
  FaMicrophone
} from "react-icons/fa";

const CompletedFreeConferance = () => {
  const [conferences, setConferences] = useState([]);
  const [search, setSearch] = useState('');
  const [filteredConferences, setFilteredConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedConference, setSelectedConference] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewMedia, setPreviewMedia] = useState({ url: '', type: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedProofDetails, setExpandedProofDetails] = useState({});
  const rowsPerPage = 10;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchConferences = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/press/free-conference/admin/completed`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Fetched completed Conferences Data:", response.data);
        const conferencesData = Array.isArray(response.data.data?.conferences) ? response.data.data.conferences : [];
        console.log("Enhanced API Response - Total Conferences:", conferencesData.length);
        conferencesData.forEach((conf, index) => {
          console.log(`Conference ${index + 1}:`, {
            conferenceId: conf.conferenceId,
            totalReporters: conf.totalReporters,
            totalCompletedReporters: conf.totalCompletedReporters,
            totalAcceptedReporters: conf.totalAcceptedReporters,
            totalPendingReporters: conf.totalPendingReporters,
            totalRejectedReporters: conf.totalRejectedReporters,
            allTargetedUsers: conf.allTargetedUsers?.length || 0,
            proofStatistics: conf.proofStatistics,
            completionMetrics: conf.completionMetrics
          });
        });
        setConferences(conferencesData);
      } catch (err) {
        setError("Failed to fetch conferences");
      } finally {
        setLoading(false);
      }
    };
    fetchConferences();
  }, []);

  useEffect(() => {
    // Sort conferences by completedAt descending (newest first)
    const sortedConferences = [...conferences].sort((a, b) => new Date(b.completedAt || b.updatedAt) - new Date(a.completedAt || a.updatedAt));
    if (!search) {
      setFilteredConferences(sortedConferences);
      setCurrentPage(1);
      return;
    }
    const lowerSearch = search.toLowerCase();
    const filtered = sortedConferences.filter(conference => {
      const conferenceId = conference.conferenceId?.toLowerCase() || '';
      const topic = conference.topic?.toLowerCase() || '';
      const state = conference.state?.toLowerCase() || '';
      const city = conference.city?.toLowerCase() || '';
      const dateStr = conference.completedAt ? new Date(conference.completedAt).toLocaleDateString("en-GB") : '';
      return (
        conferenceId.includes(lowerSearch) ||
        topic.includes(lowerSearch) ||
        state.includes(lowerSearch) ||
        city.includes(lowerSearch) ||
        dateStr.includes(lowerSearch)
      );
    });
    setFilteredConferences(filtered);
    setCurrentPage(1);
  }, [conferences, search]);

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
      <div className="flex flex-col items-center space-y-4">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-emerald-500 animate-spin"></div>
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-r-teal-500 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
          <div className="absolute inset-0 rounded-full border-4 border-transparent border-b-cyan-500 animate-spin" style={{ animationDuration: '2s' }}></div>
        </div>
        <p className="text-lg font-semibold text-gray-700">Loading Free Completed Conferences...</p>
        <p className="text-sm text-gray-500">Please wait while we fetch the data</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto text-center border border-red-200">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaTimesCircle className="text-red-500 text-2xl" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Data</h3>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  );

  // Pagination logic
  const totalPages = Math.ceil(filteredConferences.length / rowsPerPage);
  const startIdx = (currentPage - 1) * rowsPerPage;
  const endIdx = startIdx + rowsPerPage;
  const paginatedConferences = filteredConferences.slice(startIdx, endIdx);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Modern Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-2xl shadow-xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <FaMicrophone className="text-2xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Free Completed Conferences</h1>
                <p className="text-emerald-100 mt-2">View completed free conferences and their details</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl transition-colors backdrop-blur-sm"
                onClick={() => navigate("/admin/press-conference/free-conference/pending")}
              >
                <FaClock />
                <span>Pending</span>
              </button>
              <button
                className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl transition-colors backdrop-blur-sm"
                onClick={() => navigate("/admin/press-conference/free-conference/running")}
              >
                <FaPlay />
                <span>Running</span>
              </button>
              <button
                className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl transition-colors backdrop-blur-sm"
                onClick={() => navigate("/admin/press-conference/free-conference/approved")}
              >
                <FaCheckCircle />
                <span>Approved</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                  placeholder="Search by Conference ID, Topic, State, City, or Date (dd/mm/yyyy)"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>
            <button
              className="flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 px-4 py-3 rounded-xl transition-colors"
              onClick={() => setSearch('')}
            >
              <FaTimes />
              <span>Clear</span>
            </button>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <FaChartBar />
              <span>{filteredConferences.length} conferences found</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaMicrophone className="w-4 h-4" />
                      Conference ID
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaTag className="w-4 h-4" />
                      Topic
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaCalendar className="w-4 h-4" />
                      Date & Time
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaMapMarkerAlt className="w-4 h-4" />
                      Location
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaUsers className="w-4 h-4" />
                      Completion Stats
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaCheckCircle className="w-4 h-4" />
                      Status
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    <div className="flex items-center gap-2">
                      <FaEye className="w-4 h-4" />
                      Actions
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedConferences.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center">
                          <FaMicrophone className="w-10 h-10 text-emerald-500" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">No Completed Conferences Found</h3>
                          <p className="text-gray-500">There are no completed free conferences at the moment</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedConferences.map((conference) => (
                    <tr key={conference._id} className="hover:bg-gray-50 transition-colors duration-200">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">
                              {conference.conferenceId?.charAt(0)?.toUpperCase() || 'C'}
                            </span>
                          </div>
                          <span className="font-mono text-sm font-medium text-gray-900">
                            {conference.conferenceId}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="max-w-xs">
                          <p className="text-sm font-medium text-gray-900 truncate" title={conference.topic}>
                            {conference.topic}
                          </p>
                          <p className="text-xs text-gray-500 truncate" title={conference.purpose}>
                            {conference.purpose}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {conference.conferenceDate}
                          </span>
                          <span className="text-xs text-gray-500">
                            {conference.conferenceTime} {conference.timePeriod}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">{conference.city}, {conference.state}</div>
                          <div className="text-xs text-gray-500">{conference.place}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col space-y-1">
                          <div className="flex items-center space-x-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                              <FaUsers className="w-3 h-3 mr-1" />
                              {conference.completionRatio || `${conference.totalCompletedReporters || conference.completedReporters?.length || 0}/${conference.totalRespondedReporters || conference.totalReporters || 0}`}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {conference.completionPercentage ? `${conference.completionPercentage}% Complete` : (() => {
                              const completed = conference.totalCompletedReporters || conference.completedReporters?.length || 0;
                              const responded = conference.totalRespondedReporters || conference.totalReporters || 0;
                              const percentage = responded > 0 ? Math.min(((completed / responded) * 100), 100).toFixed(1) : 0;
                              return `${percentage}% Complete`;
                            })()}
                          </div>
                          <div className="text-xs text-gray-400">
                            Targeted: {conference.totalReporters || 0} |
                            Responded: {conference.totalRespondedReporters || 0} |
                            Completed: {conference.totalCompletedReporters || 0} |
                            Accepted: {conference.totalAcceptedReporters || 0} |
                            Rejected: {conference.totalRejectedReporters || 0}
                          </div>
                          {conference.completionMetrics && (
                            <div className="text-xs text-gray-400">

                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col space-y-1">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border border-green-200">
                            <FaCheckCircle className="w-3 h-3 mr-1" />
                            Completed
                          </span>
                          <span className="text-xs text-gray-500">
                            {conference.completedAt ? new Date(conference.completedAt).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          className="flex items-center space-x-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-4 py-2 rounded-xl font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                          onClick={() => {
                            setSelectedConference(conference);
                            setShowModal(true);
                          }}
                        >
                          <FaEye />
                          <span>View Details</span>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-12">
            <nav className="inline-flex items-center space-x-2 bg-white rounded-xl shadow-lg border border-gray-200 p-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 border border-gray-200 hover:border-emerald-200'
                  }`}
              >
                <span className="flex items-center gap-2">
                  <FaArrowLeft className="w-4 h-4" />
                  Previous
                </span>
              </button>

              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  const isActive = pageNum === currentPage;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 rounded-lg font-medium transition-all duration-200 ${isActive
                          ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md'
                          : 'bg-white text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 border border-gray-200 hover:border-emerald-200'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white text-gray-700 hover:bg-emerald-50 hover:text-emerald-600 border border-gray-200 hover:border-emerald-200'
                  }`}
              >
                <span className="flex items-center gap-2">
                  Next
                  <FaArrowRight className="w-4 h-4" />
                </span>
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Media Preview Modal */}
      {showPreviewModal && previewMedia.url && (
        <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => {
          setShowPreviewModal(false);
          setPreviewMedia({ url: '', type: '' });
        }}>
          <div className="max-w-4xl max-h-[90vh] relative bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                {previewMedia.type === 'video' ? <FaVideo /> : <FaImage />}
                Media Preview
              </h3>
              <button
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewMedia({ url: '', type: '' });
                }}
              >
                <FaTimesCircle className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {previewMedia.type === 'video' ? (
                <video
                  src={previewMedia.url}
                  className="max-h-[70vh] max-w-full rounded-lg"
                  controls
                  autoPlay
                />
              ) : (
                <img
                  src={previewMedia.url}
                  alt="Preview"
                  className="max-h-[70vh] max-w-full rounded-lg object-contain"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showModal && selectedConference && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-t-2xl">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <FaMicrophone />
                Conference Details
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <FaTimesCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Basic Information Section */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Conference ID</label>
                    <p className="text-gray-800 font-mono">{selectedConference.conferenceId}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Status</label>
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                      {selectedConference.status}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Topic</label>
                    <p className="text-gray-800">{selectedConference.topic}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Purpose</label>
                    <p className="text-gray-800">{selectedConference.purpose}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Date</label>
                    <p className="text-gray-800">{selectedConference.conferenceDate}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Time</label>
                    <p className="text-gray-800">{selectedConference.conferenceTime} {selectedConference.timePeriod}</p>
                  </div>
                </div>
              </div>

              {/* Location Information Section */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Location Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">State</label>
                    <p className="text-gray-800">{selectedConference.state}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">City</label>
                    <p className="text-gray-800">{selectedConference.city}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Venue</label>
                    <p className="text-gray-800">{selectedConference.place}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Landmark</label>
                    <p className="text-gray-800">{selectedConference.landmark || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Submitted By Section */}
              <div className="bg-yellow-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Submitted By</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Name</label>
                    <p className="text-gray-800">
                      {typeof selectedConference.submittedBy === 'object' && selectedConference.submittedBy?.name
                        ? selectedConference.submittedBy.name
                        : selectedConference.submittedBy || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Organization</label>
                    <p className="text-gray-800">
                      {typeof selectedConference.submittedBy === 'object' && selectedConference.submittedBy?.organization
                        ? selectedConference.submittedBy.organization
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Email</label>
                    <p className="text-gray-800">
                      {typeof selectedConference.submittedBy === 'object' && selectedConference.submittedBy?.email
                        ? selectedConference.submittedBy.email
                        : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Press ID</label>
                    <p className="text-gray-800">
                      {typeof selectedConference.submittedBy === 'object' && selectedConference.submittedBy?.pressConferenceId
                        ? selectedConference.submittedBy.pressConferenceId
                        : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Completed Reporters Section */}
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <FaUsers />
                    Completed Reporters ({selectedConference.completedReporters?.length || 0})
                  </h3>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Completion:</span>
                      <span className="font-semibold text-blue-600">
                        {selectedConference.completionRatio || `${selectedConference.totalCompletedReporters || selectedConference.completedReporters?.length || 0}/${selectedConference.totalRespondedReporters || selectedConference.totalReporters || 0}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Percentage:</span>
                      <span className="font-semibold text-green-600">
                        {selectedConference.completionPercentage ? `${selectedConference.completionPercentage}%` : (() => {
                          const completed = selectedConference.totalCompletedReporters || selectedConference.completedReporters?.length || 0;
                          const responded = selectedConference.totalRespondedReporters || selectedConference.totalReporters || 0;
                          const percentage = responded > 0 ? Math.min(((completed / responded) * 100), 100).toFixed(1) : 0;
                          return `${percentage}%`;
                        })()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Accepted:</span>
                      <span className="font-semibold text-blue-600">
                        {selectedConference.totalAcceptedReporters || 0}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Rejected:</span>
                      <span className="font-semibold text-red-600">
                        {selectedConference.totalRejectedReporters || 0}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600">Completion Date:</span>
                      <span className="font-semibold text-blue-600">
                        {selectedConference.completedAt ? new Date(selectedConference.completedAt).toLocaleDateString() : 'N/A'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800`}>
                        ✅ Conference Completed
                      </span>
                    </div>
                  </div>
                </div>

                {selectedConference.completedReporters && selectedConference.completedReporters.length > 0 ? (
                  <div className="overflow-y-auto max-h-80 space-y-3 py-2">
                    {selectedConference.completedReporters.map((reporter, index) => (
                      <div key={typeof reporter.reporterId === 'object' ? reporter.reporterId._id : reporter.reporterId || index} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">iinsaf ID</label>
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs font-bold">
                                  {reporter.iinsafId?.charAt(0)?.toUpperCase() || 'R'}
                                </span>
                              </div>
                              <p className="text-sm font-medium text-gray-800">{reporter.iinsafId}</p>
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Reporter Name</label>
                            <p className="text-sm font-medium text-gray-800">{reporter.reporterName || 'N/A'}</p>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                              <FaCheckCircle className="w-3 h-3 mr-1" />
                              Completed
                            </span>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Completed At</label>
                            <div className="flex flex-col">
                              <p className="text-sm font-medium text-gray-800">
                                {reporter.completedAt ? new Date(reporter.completedAt).toLocaleDateString() : '-'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {reporter.completedAt ? new Date(reporter.completedAt).toLocaleTimeString() : '-'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {reporter.acceptedAt && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>Accepted: {new Date(reporter.acceptedAt).toLocaleString()}</span>
                              <span>Reporter ID: {typeof reporter.reporterId === 'object' ? reporter.reporterId._id : reporter.reporterId}</span>
                            </div>
                          </div>
                        )}

                        {/* ✅ Complete Work Submitted Details */}
                        {reporter.proofDetails && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                                <FaCheckCircle className="w-4 h-4 text-green-600" />
                                Submitted Work Details
                              </h4>
                              <button
                                onClick={() => {
                                  const reporterIdStr = typeof reporter.reporterId === 'object' ? reporter.reporterId._id : reporter.reporterId;
                                  const key = `${reporterIdStr}-${reporter.iinsafId}`;
                                  setExpandedProofDetails(prev => ({
                                    ...prev,
                                    [key]: !prev[key]
                                  }));
                                }}
                                className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white px-3 py-1 rounded-lg text-xs font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                              >
                                <FaEye className="w-3 h-3" />
                                <span>
                                  {expandedProofDetails[`${typeof reporter.reporterId === 'object' ? reporter.reporterId._id : reporter.reporterId}-${reporter.iinsafId}`] ? 'Hide Details' : 'View Details'}
                                </span>
                              </button>
                            </div>

                            {/* ✅ Collapsible Proof Details */}
                            {expandedProofDetails[`${typeof reporter.reporterId === 'object' ? reporter.reporterId._id : reporter.reporterId}-${reporter.iinsafId}`] && (
                              <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {/* Screenshot */}
                                  {reporter.proofDetails.screenshot && (
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-2">Screenshot</label>
                                      <div className="relative group">
                                        <img
                                          src={reporter.proofDetails.screenshot.startsWith('http')
                                            ? reporter.proofDetails.screenshot
                                            : `${process.env.REACT_APP_API_BASE_URL}/uploads/conference-proofs/${reporter.proofDetails.screenshot.replace('\\', '/')}`}
                                          alt="Proof Screenshot"
                                          className="w-full h-32 object-cover rounded-lg border cursor-pointer hover:shadow-md transition-all duration-200"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'flex';
                                          }}
                                          onClick={() => {
                                            // ✅ Handle both Cloudinary URLs and local paths
                                            const imageUrl = reporter.proofDetails.screenshot.startsWith('http')
                                              ? reporter.proofDetails.screenshot
                                              : `${process.env.REACT_APP_API_BASE_URL}/uploads/conference-proofs/${reporter.proofDetails.screenshot.replace('\\', '/')}`;
                                            setPreviewMedia({ url: imageUrl, type: 'image' });
                                            setShowPreviewModal(true);
                                          }}
                                        />
                                        <div className="absolute inset-0 bg-gray-100 rounded-lg border flex items-center justify-center" style={{ display: 'none' }}>
                                          <div className="text-center">
                                            <FaImage className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                            <p className="text-xs text-gray-500">Image not available</p>
                                          </div>
                                        </div>
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded-lg transition-all duration-200 flex items-center justify-center">
                                          <FaEye className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Video Link Button */}
                                  {reporter.proofDetails.videoLink && (
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-2">Video Link</label>
                                      <div className="w-full h-32 bg-gradient-to-br from-red-50 to-red-100 rounded-lg border border-red-200 flex items-center justify-center">
                                        <button
                                          onClick={() => window.open(reporter.proofDetails.videoLink, '_blank')}
                                          className="flex items-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg"
                                        >
                                          <FaPlay className="w-4 h-4" />
                                          <span>Watch Video</span>
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* Channel and Platform Details */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Channel Name</label>
                                    <p className="text-sm text-gray-800 bg-gray-50 px-3 py-2 rounded-lg">
                                      {reporter.proofDetails.channelName || 'N/A'}
                                    </p>
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Platform</label>
                                    <p className="text-sm text-gray-800 bg-gray-50 px-3 py-2 rounded-lg">
                                      {reporter.proofDetails.platform || 'N/A'}
                                    </p>
                                  </div>

                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Duration</label>
                                    <p className="text-sm text-gray-800 bg-gray-50 px-3 py-2 rounded-lg">
                                      {reporter.proofDetails.duration || 'N/A'}
                                    </p>
                                  </div>
                                </div>

                                {/* Submission Details */}
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">Submitted At</label>
                                      <p className="text-sm text-gray-800">
                                        {reporter.proofDetails.submittedAt ? new Date(reporter.proofDetails.submittedAt).toLocaleString() : 'N/A'}
                                      </p>
                                    </div>

                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">Proof Status</label>
                                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${reporter.proofDetails.status === 'completed'
                                          ? 'bg-green-100 text-green-800 border border-green-200'
                                          : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                                        }`}>
                                        <FaCheckCircle className="w-3 h-3 mr-1" />
                                        {reporter.proofDetails.status || 'Unknown'}
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Admin Reject Note (if any) */}
                                {reporter.proofDetails.adminRejectNote && (
                                  <div className="mt-4 pt-4 border-t border-red-100">
                                    <label className="block text-xs font-medium text-red-600 mb-1">Admin Reject Note</label>
                                    <p className="text-sm text-red-800 bg-red-50 px-3 py-2 rounded-lg">
                                      {reporter.proofDetails.adminRejectNote}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FaUsers className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium">No completed reporters found</p>
                    <p className="text-sm text-gray-400 mt-1">Reporters who have completed their proofs will appear here</p>
                  </div>
                )}
              </div>

              {/* Pending Reporters Section */}
              {selectedConference.pendingReporters && selectedConference.pendingReporters.length > 0 && (
                <div className="bg-orange-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                      <FaClock className="w-5 h-5 text-orange-600" />
                      Pending Reporters ({selectedConference.pendingReporters.length})
                    </h3>
                    <div className="text-sm text-gray-600">
                      Reporters who were sent requests but haven't completed yet
                    </div>
                  </div>

                  <div className="overflow-y-auto max-h-60 space-y-3 py-2">
                    {selectedConference.pendingReporters.map((reporter, index) => (
                      <div key={typeof reporter.reporterId === 'object' ? reporter.reporterId._id : reporter.reporterId || index} className="bg-white rounded-lg p-4 border border-orange-200 shadow-sm">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">iinsaf ID</label>
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-xs font-bold">
                                  {reporter.iinsafId?.charAt(0)?.toUpperCase() || 'R'}
                                </span>
                              </div>
                              <p className="text-sm font-medium text-gray-800">{reporter.iinsafId}</p>
                            </div>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Reporter Name</label>
                            <p className="text-sm font-medium text-gray-800">{reporter.reporterName || 'N/A'}</p>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800 border border-orange-200">
                              <FaClock className="w-3 h-3 mr-1" />
                              {reporter.status || 'Pending'}
                            </span>
                          </div>
                        </div>

                        {reporter.acceptedAt && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>Accepted: {new Date(reporter.acceptedAt).toLocaleString()}</span>
                              <span>Reporter ID: {typeof reporter.reporterId === 'object' ? reporter.reporterId._id : reporter.reporterId}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Debug Section - Total Requests Info */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <FaUsers className="w-5 h-5 text-blue-600" />
                  Total Work Requests Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-3 border border-blue-200">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Total Requests Sent</label>
                    <p className="text-2xl font-bold text-blue-600">{selectedConference.totalReporters || 0}</p>
                    <p className="text-xs text-gray-500">Reporters who received work requests</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Completed Work</label>
                    <p className="text-2xl font-bold text-green-600">{selectedConference.totalCompletedReporters || selectedConference.completedReporters?.length || 0}</p>
                    <p className="text-xs text-gray-500">Reporters who completed their work</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-orange-200">
                    <label className="block text-sm font-medium text-gray-600 mb-1">Pending Work</label>
                    <p className="text-2xl font-bold text-orange-600">{selectedConference.totalPendingReporters || 0}</p>
                    <p className="text-xs text-gray-500">Reporters who haven't completed yet</p>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                  <label className="block text-sm font-medium text-gray-600 mb-2">Completion Rate</label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-4">
                      <div
                        className="bg-gradient-to-r from-green-500 to-emerald-500 h-4 rounded-full transition-all duration-300"
                        style={{
                          width: `${(() => {
                            const completed = selectedConference.totalCompletedReporters || selectedConference.completedReporters?.length || 0;
                            const responded = selectedConference.totalRespondedReporters || selectedConference.totalReporters || 0;
                            return responded > 0 ? Math.min(((completed / responded) * 100), 100) : 0;
                          })()}%`
                        }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-gray-700">
                      {selectedConference.completionPercentage ? `${selectedConference.completionPercentage}%` : (() => {
                        const completed = selectedConference.totalCompletedReporters || selectedConference.completedReporters?.length || 0;
                        const responded = selectedConference.totalRespondedReporters || selectedConference.totalReporters || 0;
                        const percentage = responded > 0 ? Math.min(((completed / responded) * 100), 100).toFixed(1) : 0;
                        return `${percentage}%`;
                      })()}
                    </span>
                  </div>
                </div>
              </div>


              {/* Timestamps Section */}
              <div className="bg-gray-100 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Timestamps</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Created At</label>
                    <p className="text-gray-800">{selectedConference.createdAt ? new Date(selectedConference.createdAt).toLocaleString() : 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600">Completed At</label>
                    <p className="text-gray-800">{selectedConference.completedAt ? new Date(selectedConference.completedAt).toLocaleString() : 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end p-6 border-t bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setShowModal(false)}
                className="flex items-center space-x-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-6 py-3 rounded-xl font-medium transition-all duration-200 shadow-md hover:shadow-lg"
              >
                <FaTimes />
                <span>Close</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompletedFreeConferance;
